import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { object, string } from "yup";
import {
  Type,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogTitleActions,
  Button,
  Select,
  Spinner,
  InputText,
} from "@ebscois/react-component-library";
import {
  StyledMessagesContainer,
  StyledValidationMessage,
  StyledFieldsContainer,
  StyledFieldWrapper,
  StyledSelect,
  StyledTextContainer,
  StyledCancelButton,
} from "./UserAttributesForm.styled";
import Attribute from "./Attribute";
import { useDispatch, useStore } from "../../store";
import { updateUserAttributes } from "../../store/actions/personalization";

export const VALIDATION_FAILED_MESSAGE =
  "Please correct any errors noted below.";

export const REQUIRED_MESSAGE_FIRST_NAME = "First name is required.";
export const VALIDATION_MESSAGE_FIRST_NAME = "Please enter a valid first name.";

export const REQUIRED_MESSAGE_LAST_NAME = "Last name is required.";
export const VALIDATION_MESSAGE_LAST_NAME = "Please enter a valid last name.";

export const REQUIRED_MESSAGE_EMAIL = "Email is required.";
export const VALIDATION_MESSAGE_EMAIL = "Please enter a valid email address.";

export const REQUIRED_MESSAGE_SPECIALTY = "Specialty is required.";

export const REQUIRED_MESSAGE_ROLE = "Role is required.";

const NOT_UNKNOWN_REGEX = /^(?!unknown$).+$/i;
// eslint-disable-next-line
const EMAIL_REGEX = /^(?!(?:changeme@ebsco\.com|placeholder@email\.com)$)(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;

export const ERROR_MESSAGE =
  "We're sorry but we cannot access your account information at this time. Please try again later.";

const validationSchema = object().shape({
  givenName: string()
    .required(REQUIRED_MESSAGE_FIRST_NAME)
    .matches(NOT_UNKNOWN_REGEX, VALIDATION_MESSAGE_FIRST_NAME),
  familyName: string()
    .required(REQUIRED_MESSAGE_LAST_NAME)
    .matches(NOT_UNKNOWN_REGEX, VALIDATION_MESSAGE_LAST_NAME),
  primaryEmail: string()
    .required(REQUIRED_MESSAGE_EMAIL)
    .matches(EMAIL_REGEX, VALIDATION_MESSAGE_EMAIL),
  specialty: string().required(REQUIRED_MESSAGE_SPECIALTY),
  role: string().required(REQUIRED_MESSAGE_ROLE),
});

const titleBorderOptions = {
  borderBottomColor: "gray300",
  borderBottomStyle: "solid",
  borderBottomWidth: "1px",
};

const inputProps = [
  {
    name: "givenName",
    label: "First Name",
  },
  {
    name: "familyName",
    label: "Last Name",
  },
  {
    name: "primaryEmail",
    label: "Email",
  },
];

const UserAttributesForm = ({
  isOpen,
  attributes,
  isInputEditable,
  isSelectEditable,
  handleSubmit,
  title,
  doesTitleHaveBorder,
  description,
  hasCloseButton,
  handleClose = () => {},
  onRequestClose,
}) => {
  const { givenName, familyName, primaryEmail, role, specialty } =
    attributes ?? {};
  const store = useStore();
  const dispatch = useDispatch();
  const { roles = [], specialties = [] } =
    store?.personalization?.data?.userAttributes ?? {};

  const selectProps = useMemo(
    () => [
      {
        name: "specialty",
        label: "Specialty",
        optionsArray: specialties,
      },
      {
        name: "role",
        label: "Role",
        optionsArray: roles,
      },
    ],
    [roles, specialties]
  );

  const formik = useFormik({
    initialValues: {
      givenName: givenName?.trim() || "",
      familyName: familyName?.trim() || "",
      primaryEmail: primaryEmail?.trim() || "",
      role: role?.trim() || "",
      specialty: specialty?.trim() || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const dataToUpdate = {
        ...attributes,
        givenName: values.givenName,
        familyName: values.familyName,
        primaryEmail: values.primaryEmail,
        role: values.role,
        specialty: values.specialty,
      };
      await updateUserAttributes(dispatch, dataToUpdate);
      handleSubmit();
    },
  });

  const renderText = ({ label, name }) => (
    <StyledTextContainer element="div">
      <Type element="span" fontWeight="bold" marginBottom="xs">
        {label}
      </Type>
      <Type element="span" fontSize="sm">
        {formik.values[name]}
      </Type>
    </StyledTextContainer>
  );

  const getErrorMessage = (name) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";

  const renderInput = ({ name, label }) =>
    isInputEditable(name) ? (
      <Attribute name={name} label={label} errorMessage={getErrorMessage(name)}>
        <InputText
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          placeholder={`Enter ${label}`}
        />
      </Attribute>
    ) : (
      renderText({ label, name })
    );

  const renderSelect = ({ name, label, optionsArray }) =>
    isSelectEditable(name) ? (
      <Attribute name={name} label={label} errorMessage={getErrorMessage(name)}>
        <StyledSelect
          id={name}
          label={label}
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
        >
          <Select.Option value="">{`Select ${label}`}</Select.Option>
          {optionsArray.map((item) => (
            <Select.Option key={item.replace(" ", "-")} value={item}>
              {item}
            </Select.Option>
          ))}
        </StyledSelect>
      </Attribute>
    ) : (
      renderText({ label, name })
    );

  const closeDialog = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      aria-labelledby="user-attributes-dialog-title"
      maxWidth="sm"
    >
      <DialogTitle
        id="user-attributes-dialog-title"
        element="h2"
        styledAs="h3"
        color="gray800"
        {...(doesTitleHaveBorder && titleBorderOptions)}
      >
        {title}
        {hasCloseButton && <DialogTitleActions onRequestClose={closeDialog} />}
      </DialogTitle>
      {attributes && !!Object.keys(attributes)?.length ? (
        <>
          <StyledMessagesContainer>
            {description && (
              <Type element="i" fontSize="sm" marginY="md">
                {description}
              </Type>
            )}
            {formik.isSubmitting && <Spinner size="lg" />}
            {!!Object.keys(formik.touched).length &&
              !!Object.keys(formik.errors).length && (
                <StyledValidationMessage
                  element="strong"
                  fontSize="sm"
                  color="error"
                >
                  {VALIDATION_FAILED_MESSAGE}
                </StyledValidationMessage>
              )}
          </StyledMessagesContainer>
          <form onSubmit={formik.handleSubmit}>
            <StyledFieldsContainer>
              {inputProps.map((props) => (
                <StyledFieldWrapper key={props.name}>
                  {renderInput(props)}
                </StyledFieldWrapper>
              ))}
              {selectProps.map((props) => (
                <StyledFieldWrapper key={props.name}>
                  {renderSelect(props)}
                </StyledFieldWrapper>
              ))}
            </StyledFieldsContainer>
            <DialogActions justify="center">
              <Button type="submit" disabled={formik.isSubmitting}>
                Save
              </Button>
              {hasCloseButton && (
                <StyledCancelButton kind="outlined" onClick={closeDialog}>
                  Cancel & Close
                </StyledCancelButton>
              )}
            </DialogActions>
          </form>
        </>
      ) : (
        <>
          <Type element="i" color="error" paddingX="md" paddingBottom="md">
            {ERROR_MESSAGE}
          </Type>
          <DialogActions justify="center">
            <Button kind="outlined" onClick={handleClose}>
              Okay
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

UserAttributesForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  attributes: PropTypes.object,
  isInputEditable: PropTypes.func.isRequired,
  isSelectEditable: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  doesTitleHaveBorder: PropTypes.bool,
  description: PropTypes.string,
  hasCloseButton: PropTypes.bool,
  handleClose: PropTypes.func,
  onRequestClose: PropTypes.func,
};
export default UserAttributesForm;
